import React, { ChangeEvent } from 'react';
import { range } from 'lodash';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocaleText } from '../../../locale';
import { EFontSize } from '../types';
import { styles } from '../header/ExportPdfHeaderControllers.styles';

export interface IExportPdfTableFormattingControllers {
  localeText: LocaleText;
  summaryTableFontSize: string;
  columnsPerPage: number;
  showGridlines: boolean;
  showUnderlines: boolean;
  maxColumnWidth: number;
  setShowGridlines: () => void;
  setShowUnderlines: () => void;
  setColumnsPerPage: (columnsPerRow: number) => void;
  setSummaryTableFontSize: (summaryTableFontSize: EFontSize) => void;
  setMaxColumnWidth(maxColumnWidth: number): void;
  keepRowGroupsTogetherOnPageBreaks: boolean;
  toggleKeepRowGroupsTogetherOnPageBreaks(): void;
  keepColumnGroupsTogetherOnPageBreaks: boolean;
  toggleKeepColumnGroupsTogetherOnPageBreaks(): void;
}

export const ExportPdfTableFormattingControllers: React.FC<IExportPdfTableFormattingControllers> = ({
  localeText,
  summaryTableFontSize,
  setSummaryTableFontSize,
  columnsPerPage,
  setColumnsPerPage,
  showGridlines,
  setShowGridlines,
  showUnderlines,
  setShowUnderlines,
  maxColumnWidth,
  setMaxColumnWidth,
  keepRowGroupsTogetherOnPageBreaks,
  toggleKeepRowGroupsTogetherOnPageBreaks,
  keepColumnGroupsTogetherOnPageBreaks,
  toggleKeepColumnGroupsTogetherOnPageBreaks
}) => {
  const { tableFormatting } = localeText.exportPdfModal;

  const handleMaxColumnWidthChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value ? parseInt(e.target.value, 10) : 0;
    if (!isNaN(newValue)) {
      setMaxColumnWidth(newValue);
    }
  };

  const tableFormattingFontSizes = (Object.values(EFontSize) as EFontSize[]).filter(
    (value) => value !== EFontSize.extraSmall
  );

  const fontSizeLabels = (
    fontSizeOptionsLocale: LocaleText['exportPdfModal']['tableFormatting']['fontSizeOptions'],
    fontSize: EFontSize
  ): string => {
    switch (fontSize) {
      case EFontSize.small:
        return fontSizeOptionsLocale.small;
      case EFontSize.normal:
        return fontSizeOptionsLocale.normal;
      case EFontSize.large:
        return fontSizeOptionsLocale.large;
      case EFontSize.extraLarge:
        return fontSizeOptionsLocale.extraLarge;
      default:
        return '';
    }
  };

  return (
    <FormControl fullWidth sx={{ ...styles.formControl, marginBottom: '24px' }}>
      <FormLabel id="table-formatting-title-label" sx={styles.formLabel}>
        {tableFormatting.tableFormattingTitle}
      </FormLabel>
      <Box sx={styles.formContent}>
        <TextField
          select
          label={
            <Typography variant="h4" component="label" sx={styles.typographyBase}>
              {tableFormatting.fontSizeLabel}
            </Typography>
          }
          value={summaryTableFontSize}
          onChange={(e) => {
            setSummaryTableFontSize(e.target.value as EFontSize);
          }}
          size="medium"
          data-test="summary-table-font-size-dropdown"
          fullWidth
        >
          {tableFormattingFontSizes.map((fontSize) => (
            <MenuItem key={fontSize} value={fontSize}>
              {fontSizeLabels(tableFormatting.fontSizeOptions, fontSize)}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={styles.tableFormattingColumnsSettings}>
          <TextField
            select
            sx={styles.tableFormattingColumnsPerPageField}
            label={
              <Box sx={styles.tableFormattingColumnsLabel}>
                <Typography variant="h4" component="label" sx={styles.typographyBase}>
                  {tableFormatting.columnsPerPageLabel}
                </Typography>
              </Box>
            }
            value={columnsPerPage}
            onChange={(e) => {
              setColumnsPerPage(Number(e.target.value));
            }}
            size="medium"
            fullWidth
            data-test="summary-table-columns-per-page-dropdown"
          >
            {range(1, 11).map((num) => (
              <MenuItem key={`option-${num}`} value={num}>
                {num}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            sx={styles.tableFormattingNumberInput}
            value={maxColumnWidth}
            onChange={handleMaxColumnWidthChange}
            size="medium"
            fullWidth
            type="number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={styles.typographyBase}>
                  <Typography variant="bodyDefault" sx={styles.typographyBase}>
                    {tableFormatting.maxColumnWidthPxLabel}
                  </Typography>
                </InputAdornment>
              )
            }}
            label={
              <Box sx={styles.maxColumnWidthLabel}>
                <Typography variant="h4" component="label" sx={styles.typographyBase}>
                  {tableFormatting.maxColumnWidthLabel}
                </Typography>
                <Tooltip title={tableFormatting.infoTooltip} placement="bottom" sx={styles.typographyBase}>
                  <InfoOutlinedIcon sx={styles.tooltipIcon} />
                </Tooltip>
              </Box>
            }
          />
        </Box>

        <Box sx={styles.tableLinesFormattingSettings}>
          <FormControlLabel
            sx={styles.formControlCheckbox}
            control={
              <Checkbox
                checked={showGridlines}
                onChange={setShowGridlines}
                size="medium"
                data-test="toggle-gridlines"
                sx={styles.formControlCheckbox}
              />
            }
            label={
              <Typography variant="bodyDefault" sx={styles.typographyBase}>
                {tableFormatting.showGridlinesLabel}
              </Typography>
            }
          />
          <FormControlLabel
            sx={styles.formControlCheckbox}
            control={
              <Checkbox
                checked={showUnderlines}
                onChange={setShowUnderlines}
                size="medium"
                data-test="toggle-underlines"
                sx={styles.formControlCheckbox}
              />
            }
            label={
              <Typography variant="bodyDefault" sx={styles.typographyBase}>
                {tableFormatting.showUnderlines}
              </Typography>
            }
          />
          <FormControlLabel
            sx={styles.formControlCheckbox}
            control={
              <Checkbox
                checked={keepRowGroupsTogetherOnPageBreaks}
                onChange={toggleKeepRowGroupsTogetherOnPageBreaks}
                size="medium"
                data-test="toggle-keep-row-groups-together"
                sx={styles.formControlCheckbox}
              />
            }
            label={
              <Typography variant="bodyDefault" sx={styles.typographyBase}>
                {tableFormatting.keepRowGroupsTogetherOnPageBreaksLabel}
              </Typography>
            }
          />
          <FormControlLabel
            sx={styles.formControlCheckbox}
            control={
              <Checkbox
                checked={keepColumnGroupsTogetherOnPageBreaks}
                onChange={toggleKeepColumnGroupsTogetherOnPageBreaks}
                size="medium"
                data-test="toggle-keep-column-groups-together"
                sx={styles.formControlCheckbox}
              />
            }
            label={
              <Typography variant="bodyDefault" sx={styles.typographyBase}>
                {tableFormatting.keepColumnGroupsTogetherOnPageBreaksLabel}
              </Typography>
            }
          />
        </Box>
      </Box>
    </FormControl>
  );
};
